<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-green" sprite="clock" />
          Global Mediafiles
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-green" sprite="microphone" />
            Global Mediafiles
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{enabled: true, gt: 50, oSize: 0}"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :createProcessing="createProcessing"
      :clearFormFields="clearFormFields"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
    />

    <edit-form
      :editVisible="editVisible"
      :editProcessing="editProcessing"
      :clearFormFields="clearFormFields"
      :selectedMediafile="selectedMediafile"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from 'lodash';
import feathersClient, { hostServer } from "../../../feathers/feathers-client";
import EditForm from "./components/editForm.vue";
import CreateForm from "./components/createForm";
import {globalEventBus} from "../../../globalEventBus";

export default {
  name: "GlobalMediafilesTable",
  data() {
    return {
      eventChannel: null,
      createProcessing: false,
      editProcessing: false,
      deleteProcessing: false,
      tableLoading: false,
      tableData: [],
      allFiles: [],
      total: 0,
      search: "",
      editVisible: false,
      createVisible: false,
      clearFormFields: false,
      visibleActionsId: "",
      selectedMediafile: null,
      timezones: [],
      tableColumns: [
        {
          field: 'sprite',
          title: '',
          width: 50,
          slots: {
            default: ({row}) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-green" sprite="microphone"/>
                </div>
              ]
            }
          }
        },
        {
          field: 'name',
          title: 'Name',
          minWidth: 150,
          showOverflow: true,
          sortable: true
        },
        {
          field: 'actions',
          title: '',
          minWidth: '150',
          showOverflow: false,
          slots: {
            default: ({row}) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={'edit-'+ row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={'delete-'+row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ]
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    user() {
      this.findMediaFiles();
      if(!this.eventChannel){
        this.joinSocketChannel();
      }
    },
    search: debounce( function () {
      this.searchFiles();
    }, 500)
  },

  methods: {
    ...mapActions("mediafiles", {
      deleteMediafiles: "remove"
    }),

    searchFiles() {
      if(this.search.length){
        this.tableData = this.allFiles.filter(
          data =>
            data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else{
        this.tableData = [...this.allFiles];
      }
      this.total = this.tableData.length;
    },

    //General Form and Table Operations
    cellClicked(data){
      if(data.column.property !== 'actions'){
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data){
      this.visibleActionsId = data.row.id;
    },

    rowLeave(){
      this.visibleActionsId = "";
    },

    openCreateModal() {
      this.createVisible = true;

      this.clearFormFields = false;
    },

    openEditModal(id) {
      this.clearFormFields = false;
      this.editVisible = true;

      this.editProcessing = true;
      feathersClient
        .service("mediafiles")
        .get(id)
        .then(res => {
          this.editProcessing = false;
          this.selectedMediafile = res;
        })
        .catch(error => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error"
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error"
            });
          }
          this.editProcessing = false;
        });
    },

    findMediaFiles(){
      this.tableLoading = true;
      feathersClient.service('mediafiles').find({
        query: {
          tenantId: 0
        }
      }).then( (res) => {
        this.tableData = [...res];
        this.allFiles = [...res];
        this.total = res.length;
        this.tableLoading = false;
      }).catch( () => {
        this.tableLoading = false;
      })
    },

    createOperation(formData) {
      this.$confirm("Create media file", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;
          fetch(`${hostServer}/mediafiles`, {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          })
            .then(res => res.json())
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success"
              });
              this.createProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("create");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.createProcessing = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      this.$confirm("Edit media file?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;

          fetch(`${hostServer}/mediafiles/${this.selectedMediafile.id}`, {
            method: "PATCH",
            body: editBody,
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`
            }
          })
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success"
              });
              this.editProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      this.clearFormFields = true;
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
    },

    deleteOperation(id) {
      this.$confirm("Delete media file?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.deleteProcessing = true;

          this.deleteMediafiles(id)
            .then(() => {
              this.deleteProcessing = false;
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    findTimezones(){
      feathersClient.service('timezones').find({
        query: {
          $sort: { description: 1 }
        }
      }).then(res => {
        this.timezones = [...res];
      });
    },

    joinSocketChannel(){
      const channelService = feathersClient.service('channel-memberships');
      const data = {
        roomType: 'global-admins',
      };
      channelService.create(data).then(res => {this.eventChannel = 'global-admins'});
    },

    removeListeners() {
      const userService = feathersClient.service('mediafiles');
      userService.removeListener('created');
      userService.removeListener('patched');
      userService.removeListener('removed');
      globalEventBus.$off('appReconnected');
    },
  },

  created(){
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //record was edited
    feathersClient.service("mediafiles").on("patched", data => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allFiles.findIndex(
        item => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allFiles.splice(updateAllIndex, 1, data);
      }
    });

    //record was created
    feathersClient.service("mediafiles").on("created", data => {
      this.tableData.push(data);
      this.allFiles.push(data);
    });

    //record was deleted
    feathersClient.service("mediafiles").on("removed", data => {
      const indexToRemove = this.tableData.findIndex(
        item => item.id === data.id
      );
      const indexAllToRemove = this.allFiles.findIndex(
        item => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allFiles.splice(indexAllToRemove, 1);
      }
    });
  },

  mounted() {
    if (this.user) {
      this.findMediaFiles();
      this.joinSocketChannel();
    }
    this.findTimezones();
  },

  beforeDestroy() {
    feathersClient.service('channel-memberships').remove(null, {query: {channelName: this.eventChannel}});
    this.removeListeners();
  }
};
</script>
